import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Switch,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  getAIConfig,
  updateAIConfig,
  type AIConfig,
  type AIProvider,
  type FunctionCallingMode,
} from '../services/aiConfigService';

const OPENAI_MODEL_SUGGESTIONS = [
  'gpt-5-mini',
  'gpt-5.2',
  'gpt-5-nano',
  'gpt-4.1-mini',
];

const GEMINI_MODEL_SUGGESTIONS = [
  'gemini-3.5-flash',
  'gemini-3-flash-preview',
  'gemini-2.5-flash',
  'gemini-2.5-pro',
  'gemini-2.5-flash-lite',
  'gemini-3.1-pro-preview',
];

const FUNCTION_CALLING_MODES: FunctionCallingMode[] = ['AUTO', 'ANY', 'NONE', 'VALIDATED'];

const AISettings: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  // Local editable state
  const [provider, setProvider] = React.useState<AIProvider>('openai');
  const [openaiModel, setOpenaiModel] = React.useState<string>('');
  const [openaiTemperature, setOpenaiTemperature] = React.useState<string>('0.2');
  const [geminiModel, setGeminiModel] = React.useState<string>('');
  const [geminiTemperature, setGeminiTemperature] = React.useState<string>('0.2');
  const [geminiThinkingBudget, setGeminiThinkingBudget] = React.useState<string>('0');
  const [geminiFunctionCallingMode, setGeminiFunctionCallingMode] = React.useState<FunctionCallingMode>('AUTO');
  const [allowlist, setAllowlist] = React.useState<string>(''); // one UID per line
  const [forceProviderForAllowlist, setForceProviderForAllowlist] = React.useState<AIProvider | ''>('');
  const [enabled, setEnabled] = React.useState<boolean>(true);
  const [maintenanceMessage, setMaintenanceMessage] = React.useState<string>('Assistant IA bientôt disponible. Merci de réessayer plus tard.');

  const load = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const cfg: AIConfig = await getAIConfig();

      setProvider(cfg.provider ?? 'openai');

      setOpenaiModel(cfg.openai?.model ?? OPENAI_MODEL_SUGGESTIONS[0]);
      setOpenaiTemperature(
        typeof cfg.openai?.temperature === 'number' ? String(cfg.openai.temperature) : '0.2'
      );

      setGeminiModel(cfg.gemini?.model ?? GEMINI_MODEL_SUGGESTIONS[0]);
      setGeminiTemperature(
        typeof cfg.gemini?.temperature === 'number' ? String(cfg.gemini.temperature) : '0.2'
      );
      setGeminiThinkingBudget(
        typeof cfg.gemini?.thinkingBudget === 'number' ? String(cfg.gemini.thinkingBudget) : '0'
      );
      setGeminiFunctionCallingMode(cfg.gemini?.functionCallingMode ?? 'AUTO');

      const list = (cfg.rollout?.userAllowlist ?? []).filter((x) => typeof x === 'string' && x.trim().length > 0);
      setAllowlist(list.join('\n'));
      setForceProviderForAllowlist((cfg.rollout?.forceProviderForAllowlist as AIProvider) ?? '');

      // Kill switch + message
      setEnabled(cfg.enabled !== false);
      setMaintenanceMessage(
        typeof cfg.maintenanceMessage === 'string'
          ? cfg.maintenanceMessage
          : 'Assistant IA bientôt disponible. Merci de réessayer plus tard.'
      );
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement de la configuration IA.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void load();
  }, []);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Build partial object to merge
      const payload: Partial<AIConfig> = {
        provider,
        enabled,
        maintenanceMessage: maintenanceMessage.trim(),
        openai: {
          model: openaiModel.trim(),
          temperature: Number(openaiTemperature),
        },
        gemini: {
          model: geminiModel.trim(),
          temperature: Number(geminiTemperature),
          thinkingBudget: Number(geminiThinkingBudget),
          functionCallingMode: geminiFunctionCallingMode,
        },
        rollout: {
          userAllowlist: allowlist
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length > 0),
          forceProviderForAllowlist: (forceProviderForAllowlist || null) as any,
        },
      };

      // Basic validation
      if (!payload.openai?.model) throw new Error('Modèle OpenAI requis.');
      if (!payload.gemini?.model) throw new Error('Modèle Gemini requis.');

      await updateAIConfig(payload);
      setSuccess('Configuration IA enregistrée avec succès.');
      // Refresh local in case server adjusted anything
      setTimeout(() => {
        void load();
      }, 500);
    } catch (e: any) {
      setError(e?.message || 'Échec de l’enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const handleProviderChange = (e: SelectChangeEvent) => {
    setProvider(e.target.value as AIProvider);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        AI Settings
      </Typography>

      {loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Chargement…</Typography>
        </Stack>
      ) : (
        <Box component="form" noValidate autoComplete="off">
          <Stack spacing={2} sx={{ mb: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
          </Stack>

          {/* Activation */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">Activation</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              <Typography>{enabled ? 'Assistant IA activé' : 'Assistant IA désactivé'}</Typography>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Message affiché au client quand l’IA est désactivée"
              value={maintenanceMessage}
              onChange={(e) => setMaintenanceMessage(e.target.value)}
            />
          </Stack>

          {/* Provider */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">Fournisseur</Typography>
            <FormControl fullWidth>
              <InputLabel id="provider-label">Fournisseur</InputLabel>
              <Select
                labelId="provider-label"
                label="Fournisseur"
                value={provider}
                onChange={handleProviderChange}
              >
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="gemini">Gemini</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {/* OpenAI */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">OpenAI</Typography>
            <FormControl fullWidth>
              <InputLabel id="openai-model-label">Modèle OpenAI</InputLabel>
              <Select
                labelId="openai-model-label"
                label="Modèle OpenAI"
                value={openaiModel}
                onChange={(e) => setOpenaiModel(e.target.value)}
              >
                {OPENAI_MODEL_SUGGESTIONS.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Modèle OpenAI (personnalisé)"
              placeholder="Entrer un modèle manuellement si non listé"
              value={openaiModel}
              onChange={(e) => setOpenaiModel(e.target.value)}
            />
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: '0.1' }}
              label="Température OpenAI"
              value={openaiTemperature}
              onChange={(e) => setOpenaiTemperature(e.target.value)}
            />
          </Stack>

          {/* Gemini */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">Gemini</Typography>
            <FormControl fullWidth>
              <InputLabel id="gemini-model-label">Modèle Gemini</InputLabel>
              <Select
                labelId="gemini-model-label"
                label="Modèle Gemini"
                value={geminiModel}
                onChange={(e) => setGeminiModel(e.target.value)}
              >
                {GEMINI_MODEL_SUGGESTIONS.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Modèle Gemini (personnalisé)"
              placeholder="Entrer un modèle manuellement si non listé"
              value={geminiModel}
              onChange={(e) => setGeminiModel(e.target.value)}
            />
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: '0.1' }}
              label="Température Gemini"
              value={geminiTemperature}
              onChange={(e) => setGeminiTemperature(e.target.value)}
            />
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: '1', min: '0' }}
              label="Thinking Budget (0 pour désactiver)"
              value={geminiThinkingBudget}
              onChange={(e) => setGeminiThinkingBudget(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="fc-mode-label">Function Calling Mode</InputLabel>
              <Select
                labelId="fc-mode-label"
                label="Function Calling Mode"
                value={geminiFunctionCallingMode}
                onChange={(e) => setGeminiFunctionCallingMode(e.target.value as FunctionCallingMode)}
              >
                {FUNCTION_CALLING_MODES.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Rollout */}
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">Rollout (optionnel)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Liste blanche d'UID (un par ligne)"
              placeholder="uid1&#10;uid2&#10;uid3"
              value={allowlist}
              onChange={(e) => setAllowlist(e.target.value)}
            />
            <FormControl fullWidth>
              <InputLabel id="force-provider-label">Forcer le provider pour la liste blanche</InputLabel>
              <Select
                labelId="force-provider-label"
                label="Forcer le provider pour la liste blanche"
                value={forceProviderForAllowlist}
                onChange={(e) => setForceProviderForAllowlist(e.target.value as AIProvider | '')}
              >
                <MenuItem value="">Aucun</MenuItem>
                <MenuItem value="openai">OpenAI</MenuItem>
                <MenuItem value="gemini">Gemini</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => void load()}
              disabled={saving}
            >
              Recharger
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default AISettings;
